import { api, ApiError, ID, useBaseMutation, useBaseQuery } from 'api'
import { Placement } from 'api/placement/types'
import { getMessageFromApiError } from 'api/shared/utils'
import { VirtualCurrency } from 'api/virtual-currency/types'
import { useToast } from 'components/shared/Snackbars'
import { useQueryClient } from 'react-query'
import { usePublisherStore } from 'store/publisher'
import CREATE_APPLICATION from './queries/create'
import GET_APPLICATION from './queries/get'
import GET_ALL_PUBLISHER_APPS from './queries/getAll'
import UPDATE_APPLICATION from './queries/update'
import { Application } from './types'

interface GetData {
  application: Application
}

interface GetVariables {
  id: ID
}

export function useGetApplication(variables: GetVariables) {
  return useBaseQuery<GetData, GetVariables>(
    ['application', { id: variables.id }],
    GET_APPLICATION,
    variables
  )
}

interface GetAllData {
  applications: Application[]
}

type GetAllVariables = {
  publisherId: ID
}

export function useGetAllApplications() {
  const publisherId = usePublisherStore((state) => state.publisherId)
  return useBaseQuery<GetAllData, GetAllVariables>(
    ['applications', publisherId],
    GET_ALL_PUBLISHER_APPS,
    { publisherId }
  )
}

interface CreateData {
  applicationCreate: {
    application: Application
  }
}

type CreateVariables = Partial<Application> & {
  placementAttributes?: Partial<Placement>
  virtualCurrencyAttributes?: Partial<VirtualCurrency>
}

export function useCreateApplication() {
  const queryClient = useQueryClient()
  const { showSuccess, showError } = useToast()

  return useBaseMutation<CreateData, ApiError, CreateVariables>(
    (variables) =>
      api.request<CreateData, CreateVariables>(CREATE_APPLICATION, variables),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('applications')
        showSuccess('The application was created')
      },
      onError: (error) => showError(getMessageFromApiError(error)),
    }
  )
}

interface UpdateData {
  applicationUpdate: {
    application: Application
  }
}

type UpdateVariables = Partial<Application>

export function useUpdateApplication() {
  const queryClient = useQueryClient()
  const { showSuccess, showError } = useToast()

  return useBaseMutation<UpdateData, ApiError, UpdateVariables>(
    (variables) =>
      api.request<UpdateData, UpdateVariables>(UPDATE_APPLICATION, variables),
    {
      onSuccess: (variables) => {
        queryClient.invalidateQueries('applications')
        queryClient.invalidateQueries([
          'application',
          { id: variables.applicationUpdate.application.id },
        ])
        showSuccess('The application was updated')
      },
      onError: (error) => showError(getMessageFromApiError(error)),
    }
  )
}
